import { handleError } from "@/api/handleError";
import { getBusinessBalance } from "@/api/requests/business";
import useAnalytics from "@/composables/useAnalytics";
import { sendUserActivityEvent } from "@/helpers/integrations/analytics/backend-events";
import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
import { INBOUND_CALL_EVENTS } from "@/helpers/integrations/analytics/user-activity-events";
import emitter from "@/helpers/integrations/emitter";
import { updateOnboardingStage } from "@/helpers/queries/personalization";
import { getReceiverNumbers } from "@/helpers/reports";
import { useBusinessStore } from "@/stores/businesses.store";
import { useNotesStore } from "@/stores/notes.store";
import { useNumberStore } from "@/stores/numbers.store";
import { useTeamStore } from "@/stores/team-member.store";
import { useUtilsStore } from "@/stores/utils.store";
import { PressOne } from "@pressone/dialer";
import type {
  CallPhase,
  CallStackState,
  PressOneOptions,
  PressOneSipCredentialInstance,
} from "@pressone/dialer/lib/types";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";
import { useCallStore } from "./call.store";

const dialerWorker = new Worker("/dialer-worker.js");

const isChromiumBasedBrowser = () => {
  if (!navigator.userAgent) {
    return false;
  }

  const isChromiumBased =
    /Chrome/.test(navigator.userAgent) || /Edg/.test(navigator.userAgent);

  if (isChromiumBased) {
    return true;
  }

  return false;
};

export const callQueueWaitingTime = 15; // 15 seconds

// Types for our dialer state
interface DialerUIState {
  showDialerDrawer: boolean;
  showIncomingCallNotification: boolean;
  showOutgoingCallNotification: boolean;
  showActiveCallInterface: boolean;
  showDTMFPad: boolean;
  isActiveCallInterfaceMinimized: boolean;
  showDialerModal: boolean;
  showActiveCallInterfaceButtons: boolean;
  showOutboundModal: boolean;
}

interface DialerCallState {
  callDirection: "incoming" | "outgoing" | null;
  callDuration: number;
  callPhase: CallPhase | null;
  numberToCall: string;
  getOutgoingXCallID: string | null;
  getInboundXCallID: string | null;

  isFetchingBalance: boolean;
  activeTimeouts: Set<NodeJS.Timeout>;

  isMuted: boolean;
  isHeld: boolean;
  isSpeakerOff: boolean;
  callVolume: number;
  micVolume: number;
  showVolumeControls: boolean;
}

interface CallQueueState {
  callTriggeredBy: "regular_call" | "outbound_call_queue";
  callQueueWaitingTime: number;
  callQueueWaitingTimer: NodeJS.Timeout | null;
}

// Add initialization state interface
interface DialerInitializationState {
  isInitialized: boolean;
  isConnecting: boolean;
  connectionError: string | null;
}

// Add individual connection status interface
interface SipConnectionStatus {
  isConnected: boolean;
  isConnecting: boolean;
}

const initialDialerUIState: DialerUIState = {
  showIncomingCallNotification: false,
  showOutgoingCallNotification: false,
  showActiveCallInterface: false,
  showDTMFPad: false,
  isActiveCallInterfaceMinimized: false,
  showDialerModal: false,
  showActiveCallInterfaceButtons: false,
  showOutboundModal: false,
  showDialerDrawer: false,
};

const initialDialerCallState: DialerCallState = {
  callDirection: null,
  callDuration: 0,
  callPhase: null,
  numberToCall: "",
  getOutgoingXCallID: null,
  getInboundXCallID: null,

  isFetchingBalance: false,
  activeTimeouts: new Set(),

  isMuted: false,
  isHeld: false,
  isSpeakerOff: false,
  callVolume: 80,
  micVolume: 100,
  showVolumeControls: false,
};

const initialCallQueueState: CallQueueState = {
  callTriggeredBy: "regular_call",
  callQueueWaitingTime: callQueueWaitingTime,
  callQueueWaitingTimer: null,
};

const initialDialerCallStackState: CallStackState = {
  calls: [],
  activeCallId: undefined,
  activeCall: undefined,
  queuedCount: 0,
  heldCount: 0,
  totalCount: 0,
};

const initialDialerInitializationState: DialerInitializationState = {
  isInitialized: false,
  isConnecting: false,
  connectionError: null,
};

const initialPressOneDialer: PressOne | null = null;

const initialDialerSipCredentials: PressOneSipCredentialInstance = {};

const initialDialerSipConnectionState: { [key: string]: SipConnectionStatus } =
  {};

const initialDialerDtmfInput: (number | string)[] = [];

const callEndCleanupDelay = 3000;

export const useDialerV2Store = defineStore("dialerV2", () => {
  // Store initializations - moved to top for consistency
  const businessStore = useBusinessStore();
  const numberStore = useNumberStore();
  const utilsStore = useUtilsStore();
  const noteStore = useNotesStore();
  const teamStore = useTeamStore();
  const callStore = useCallStore();
  const analytics = useAnalytics();

  // Initialization state
  const initializationState = ref<DialerInitializationState>({
    ...initialDialerInitializationState,
  });

  // UI state
  const uiState = ref<DialerUIState>({
    ...initialDialerUIState,
  });

  // Call stack state
  const callStackState = shallowRef<CallStackState>({
    ...initialDialerCallStackState,
  });

  // Call state
  const callState = ref<DialerCallState>({
    ...initialDialerCallState,
  });

  // Call queue state
  const callQueueState = ref<CallQueueState>({
    ...initialCallQueueState,
  });

  // PressOne dialer instance
  const pressoneDialer = shallowRef<PressOne | null>(initialPressOneDialer);

  // SIP credentials
  const sipCredentials = ref<PressOneSipCredentialInstance>({
    ...initialDialerSipCredentials,
  });

  // Individual connection status for each instance key
  const sipConnectionState = ref<{ [key: string]: SipConnectionStatus }>({
    ...initialDialerSipConnectionState,
  });

  // DTMF state
  const dtmfInput = ref<(number | string)[]>(initialDialerDtmfInput);

  // Business logic computed properties
  const activeCallBusinessId = computed(() => {
    const key = numberStore.currentUserReceiverKey?.toString();

    if (!key) {
      return null;
    }

    return sipCredentials.value && key in sipCredentials.value
      ? sipCredentials.value[key].business_id
      : null;
  });

  const callInfo = computed(() => {
    const businessNumberId = numberStore.currentUserReceiverKey;

    const activeNumberInfo = numberStore.numbers?.find(
      (number) => number.id === Number(businessNumberId)
    );

    const callId =
      callState.value.callDirection === "incoming"
        ? callState.value.getInboundXCallID
        : callState.value.getOutgoingXCallID;

    return {
      call_id: callId || "",
      did: activeNumberInfo?.business_number?.phone_number,
    };
  });

  const canMakeCall = computed(() => {
    const subscriptionStatus = numberStore.subscriptionStatus || "";

    const allowedStatuses = ["active", "upcoming_renewal"];

    return allowedStatuses.includes(subscriptionStatus);
  });

  const activePhoneConnectionStatus = computed(() => {
    const activeInstanceKey = numberStore.currentUserReceiverKey?.toString();

    if (!activeInstanceKey) {
      return {
        isConnected: false,
        isConnecting: false,
      };
    }

    return sipConnectionState.value[activeInstanceKey];
  });

  // Connection status computed properties
  const phonesConnected = computed(() => {
    // Safely calculate total SIP credentials
    const total = sipCredentials.value
      ? Object.keys(sipCredentials.value).length
      : 0;

    // Early return if not registered or no credentials
    if (!sipConnectionState.value || total === 0) {
      return {
        status: false,
        connected: 0,
        total,
      };
    }

    // Count connected phones
    let connected = 0;

    for (const key in sipConnectionState.value) {
      if (Object.prototype.hasOwnProperty.call(sipConnectionState.value, key)) {
        if (sipConnectionState.value[key]?.isConnected === true) {
          connected++;
        }
      }
    }

    return {
      status: connected === total && total > 0, // Ensure status reflects actual connection state
      connected,
      total,
    };
  });

  // Get live call connection status (for connection table)
  const liveCallConnectionStatus = computed(() => {
    // Check if we have any live widget credentials (business_id === 999)
    const liveWidgetCreds = Object.entries(sipCredentials.value).find(
      ([, creds]) => creds.business_id === 999
    );

    if (liveWidgetCreds) {
      const liveWidgetKey = liveWidgetCreds[0];
      return sipConnectionState.value[liveWidgetKey] || null;
    }

    return null;
  });

  // Get no trunk credentials (live widget credentials)
  const noTrunkCreds = computed(() => {
    for (const [key, creds] of Object.entries(sipCredentials.value)) {
      if (creds.business_id === 999) {
        return {
          [key]: creds,
        };
      }
    }
    return null;
  });

  const callStatus = computed(() => {
    const callPhase = callState.value.callPhase;
    const callDirection = callState.value.callDirection;

    if (!callPhase) {
      return "";
    }

    switch (callPhase) {
      case "CONNECTING":
        return callDirection === "outgoing" ? "Calling..." : "Connecting...";
      case "RINGING":
        return callDirection === "incoming" ? "Incoming Call" : "Ringing...";
      case "ACTIVE":
        return "In Call";
      case "HELD":
        return "On Hold";
      default:
        return "Call Ended";
    }
  });

  const updateCallStateFromStack = (stack: CallStackState) => {
    const hasCalls = stack.calls.length > 0;

    // The active call is not updated in the stack for incoming calls, this is a workaround
    if (hasCalls && !stack.activeCall) {
      const newStack = {
        ...stack,
        activeCall: stack.calls[0],
        activeCallId: stack.calls[0].id,
      };

      callStackState.value = newStack;

      return;
    }

    callStackState.value = stack;
  };

  const resetCallState = () => {
    callStackState.value = {
      ...initialDialerCallStackState,
    };

    callState.value = {
      ...initialDialerCallState,
    };

    uiState.value = {
      ...initialDialerUIState,
    };

    dtmfInput.value = initialDialerDtmfInput;
  };

  const resetInitializationState = () => {
    initializationState.value = {
      ...initialDialerInitializationState,
    };
  };

  const createTransientCallRecord = () => {
    callStore.transientCallRecord = {
      recipient_number: callState.value.numberToCall,
      is_dialed: callState.value.callDirection === "outgoing",
      start_time: new Date().toISOString().replace("Z", "+00:00"),
      caller_name: "",
      transient: true,
      date_created: new Date().toISOString(),
      last_modified: new Date().toISOString(),
      duration_secs: 0,
      cost: 0, // Add default cost value
      receiver_name: "", // Add default receiver_name value
    };
  };

  const saveTransientCallRecord = () => {
    if (callStore.transientCallRecord) {
      callStore.transientCallRecord.duration_secs =
        callState.value.callDuration || 0;

      if (noteStore.activeCallNote) {
        callStore.transientCallRecord.noteRecord = noteStore.activeCallNote;
      }

      if (callStore.transientCallRecord) {
        const isOutgoing = callState.value.callDirection === "outgoing";

        callStore.transientCallRecord.sip_call_id = isOutgoing
          ? (callState.value.getOutgoingXCallID ?? undefined)
          : (callState.value.getInboundXCallID ?? undefined);

        if (callStore.callList) {
          callStore.callList.data = [
            callStore.transientCallRecord,
            ...(callStore.callList?.data || []),
          ];
        }
      }
    }
  };

  const clearAllAudioChannel = () => {
    // End all audio streams
    for (const key in pressoneDialer.value?.audioChannels) {
      const audioElement = document.createElement("audio");

      audioElement.src = pressoneDialer.value?.audioChannels[key].currentSrc;

      if (audioElement.src === "https://outlinks.pressone.co/p0p") {
        if (!audioElement.paused) {
          audioElement.pause();
        }
        audioElement.src = "";
        audioElement.srcObject = null;
      }
    }
  };

  // Helper methods for timeout management
  const addTimeout = (timeout: NodeJS.Timeout): NodeJS.Timeout => {
    callState.value.activeTimeouts.add(timeout);
    return timeout;
  };

  const removeTimeout = (timeout: NodeJS.Timeout): void => {
    clearTimeout(timeout);
    callState.value.activeTimeouts.delete(timeout);
  };

  const clearAllTimeouts = (): void => {
    callState.value.activeTimeouts.forEach((timeout) => {
      removeTimeout(timeout);
    });
    callState.value.activeTimeouts.clear();
  };

  const updateBusinessBalance = async () => {
    const businessId = businessStore.currentBusinessId;

    try {
      if (!businessId) {
        throw new Error("Business id not found");
      }

      callState.value.isFetchingBalance = true;
      const response = await getBusinessBalance({ businessId });
      businessStore.currentBalance = response?.data.balance;
    } catch (error) {
      handleError({ error });
    } finally {
      callState.value.isFetchingBalance = false;
    }
  };

  const scheduleBalanceUpdate = (): void => {
    const timeout = addTimeout(
      setTimeout(() => {
        void updateBusinessBalance();
        callState.value.activeTimeouts.delete(timeout);
      }, 15000)
    );
  };

  const scheduleCallCleanup = (delay: number): void => {
    const timeout = addTimeout(
      setTimeout(() => {
        businessStore.finishTask("call");

        saveTransientCallRecord();

        noteStore.callnote = "";
        noteStore.setNoteCounter = false;

        const currentNumberToCall = callState.value.numberToCall;
        const currentCallDirection = callState.value.callDirection;

        resetCallState();

        if (currentCallDirection === "outgoing") {
          // Show the outbound modal again after the call is ended for outgoing calls
          callState.value.numberToCall = currentNumberToCall;
          uiState.value.showOutboundModal = true;
        }

        callState.value.activeTimeouts.delete(timeout);
      }, delay)
    );
  };

  const setupTestCallTimeout = (): void => {
    if (!businessStore.activeBusiness?.verified) {
      const timeout = addTimeout(
        setTimeout(() => {
          void pressoneDialer.value?.endCall();
          callState.value.activeTimeouts.delete(timeout);
        }, 30000)
      );
    }
  };

  const setupEventListeners = () => {
    if (!pressoneDialer.value) {
      handleError({
        error: new Error("Dialer not initialized"),
        silent: true,
      });

      return;
    }

    // Registration events - handle individual instance keys
    pressoneDialer.value.on("registered", ({ instanceKey }) => {
      // Update individual instance connection status
      sipConnectionState.value[instanceKey] = {
        isConnected: true,
        isConnecting: false,
      };

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.SIP_CONNECTED);
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    pressoneDialer.value.on("unregistered", ({ instanceKey }) => {
      // Update individual instance connection status
      sipConnectionState.value[instanceKey] = {
        isConnected: false,
        isConnecting: false,
      };

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.SIP_DISCONNECTED);
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    pressoneDialer.value.on("registration-failed", ({ instanceKey, cause }) => {
      handleError({
        error: new Error(`Registration failed for ${instanceKey}: ${cause}`),
        silent: true,
      });

      // Update individual instance connection status
      sipConnectionState.value[instanceKey] = {
        isConnected: false,
        isConnecting: false,
      };

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.SIP_CONNECTION_ERR);
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    // Call stack events
    pressoneDialer.value.on("call-stack-changed", (state: CallStackState) => {
      updateCallStateFromStack({ ...state });
    });

    // Call phase changes
    pressoneDialer.value.on("call-phase-changed", ({ call, newPhase }) => {
      // Only update the call phase if it's the active call
      if (call.id !== callStackState.value?.activeCall?.id) {
        return;
      }

      // Handled in call-ended/call-failed event handler
      // This is due to an issue where incoming calls ends and restarts repeatedly
      // And then the phase no longer changes from ENDED
      if (newPhase !== "ENDED") {
        callState.value.callPhase = newPhase;
      }

      try {
        // Handle ACTIVE phase
        if (newPhase === "ACTIVE") {
          uiState.value.showActiveCallInterfaceButtons = true;
          uiState.value.showActiveCallInterface = true;

          callState.value.numberToCall = call.peer;
          callState.value.callDuration = call.duration;

          // For outgoing calls, track the call ID
          if (call.direction === "outgoing") {
            callState.value.getOutgoingXCallID = call.callId;
            uiState.value.showOutgoingCallNotification = false;
          }

          // For incoming calls, send pickup successful event
          if (call.direction === "incoming") {
            callState.value.getInboundXCallID = call.callId;
            uiState.value.showIncomingCallNotification = false;

            void sendUserActivityEvent(
              INBOUND_CALL_EVENTS.CALL_PICKUP_SUCCESSFUL,
              callInfo.value
            );
          }

          // Setup test call timeout
          if (pressoneDialer.value) {
            setupTestCallTimeout();
          }

          const businessNumber = activeCallBusinessId.value?.toString();

          if (businessNumber) {
            void getReceiverNumbers(businessNumber).then((agents) => {
              teamStore.getRecieversUnderABusiness = agents.data;
            });
          }
        }
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    // Call duration updates
    pressoneDialer.value.on("call-duration-tick", ({ call, duration }) => {
      if (call.id === callStackState.value.activeCall?.id) {
        callState.value.callDuration = duration;
      }
    });

    // pressoneDialer.value.on(
    //   "call-promoted",
    //   ({ call, previousActiveCall, policy }) => {
    //     console.log("call-promoted", call, previousActiveCall, policy);
    //   }
    // );

    // Incoming call events
    pressoneDialer.value.on("incoming-call", ({ call }) => {
      businessStore.startTask("call");

      // Remove all scheduled timeouts
      // This is important to avoid closing the dialer when the call is incoming again
      clearAllTimeouts();

      createTransientCallRecord();

      // If already in a call, skip as they will be queued
      if (callStackState.value.activeCallId) {
        return;
      }

      // Sometimes incoming calls come as QUEUED, this is a workaround instead of call.phase
      callState.value.callPhase = "RINGING";
      callState.value.callDirection = call.direction;
      callState.value.numberToCall = call.peer;
      callState.value.getInboundXCallID = call.callId;

      uiState.value.showDialerModal = true;
      uiState.value.showIncomingCallNotification = true;

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.OPEN_DIALLER);

        void sendUserActivityEvent(INBOUND_CALL_EVENTS.INBOUND_CALL_RECEIVED, {
          call_id: call.callId,
          did: call.peer,
          is_active_tab: document.visibilityState === "visible",
        });
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    // Outgoing call events
    pressoneDialer.value.on("outgoing-call", ({ call }) => {
      businessStore.startTask("call");

      callState.value.callPhase = call.phase;
      callState.value.callDirection = call.direction;
      callState.value.numberToCall = call.peer;
      callState.value.getOutgoingXCallID = call.callId;

      uiState.value.showDialerModal = true;
      uiState.value.showOutgoingCallNotification = true;

      createTransientCallRecord();

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.OPEN_DIALLER);
      } catch (error) {
        handleError({ error, silent: true });
      }
    });

    // Call end events
    pressoneDialer.value.on("call-ended", ({ call }) => {
      // Only update the call phase if it's the active call
      if (call.id === callStackState.value.activeCallId) {
        callState.value.callPhase = "ENDED";
      }

      void (async () => {
        try {
          // Analytics for long incoming calls
          if (
            callState.value.callDirection === "incoming" &&
            callState.value.callDuration >= 10
          ) {
            void sendUserActivityEvent(
              INBOUND_CALL_EVENTS.INBOUND_CALL_CONNECTED_10s_PLUS,
              callInfo.value
            );
          }

          void analytics.logEvent(AMPLITUDE_EVENTS.CLOSED_DIALLER);

          if (callState.value.callDirection === "outgoing") {
            callState.value.isFetchingBalance = true;
            scheduleBalanceUpdate();
          }

          // Handle test call events
          if (!businessStore.activeProfile?.business?.verified) {
            if (callState.value.callDuration >= 5) {
              void analytics.logEvent(AMPLITUDE_EVENTS.TEST_CALL_SUCCESSFUL);
              await updateOnboardingStage({ has_made_call: true });
              utilsStore.madeFirstCall = true;
            } else if (callState.value.callDuration <= 2) {
              void analytics.logEvent(AMPLITUDE_EVENTS.TEST_CALL_FAILED);
            }
          }

          await noteStore.saveActiveCallNote();

          emitter.emit("getCallHistory");

          const totalCount = callStackState.value.totalCount;

          // Reset call state when all calls end
          // Including the active call hence the +1
          if (!totalCount || totalCount <= 1) {
            // Close DTMF pad immediately
            uiState.value.showDTMFPad = false;

            scheduleCallCleanup(callEndCleanupDelay);
          }
        } catch (error) {
          handleError({ error, silent: true });
        }
      })();
    });

    // Call failed events
    pressoneDialer.value.on("call-failed", ({ call }) => {
      // Only update the call phase if it's the active call
      if (call.id === callStackState.value.activeCallId) {
        callState.value.callPhase = "ENDED";
      }

      try {
        void analytics.logEvent(AMPLITUDE_EVENTS.CLOSED_DIALLER);

        emitter.emit("getCallHistory");

        const totalCount = callStackState.value.totalCount;

        // Reset call state when all calls end
        // Including the active call hence the +1
        if (!totalCount || totalCount <= 1) {
          scheduleCallCleanup(callEndCleanupDelay);
        }
      } catch (error) {
        handleError({ error, silent: true });
      }
    });
  };

  // Actions
  const initializeDialer = (
    credentials: PressOneSipCredentialInstance,
    options?: Partial<PressOneOptions>
  ) => {
    try {
      sipCredentials.value = credentials;

      // Initialize isRegistered state for all instance keys
      const instanceKeys = Object.keys(credentials);

      for (const key of instanceKeys) {
        sipConnectionState.value[key] = {
          isConnected: false,
          isConnecting: false,
        };
      }

      const pressoneInstance = new PressOne(
        { sipCredentials: credentials },
        {
          debug: false,
          worker: dialerWorker,
          autoRejectIncomingCallsWhenSessionOngoing: false,
          platformKind: "web",
          registrationExpiry: 60,
          autoResumeHeldCalls: true,
          ...(isChromiumBasedBrowser() && {
            iceExitOnFirstCandidate: true,
            iceGatheringTimeoutMs: 10000,
          }),
          ...options,
        }
      );

      pressoneDialer.value = pressoneInstance;

      // Setup event listeners
      setupEventListeners();

      // Set initialization state
      initializationState.value.isInitialized = true;
      initializationState.value.connectionError = null;

      return pressoneInstance;
    } catch (error) {
      initializationState.value.connectionError = "Failed to initialize dialer";

      throw new Error(`Failed to initialize dialer: ${JSON.stringify(error)}`);
    }
  };

  const startDialer = async (
    defaultInstanceKey: string,
    instanceKeysToStart: string[]
  ) => {
    if (!pressoneDialer.value) {
      throw new Error("Dialer not initialized");
    }

    if (!initializationState.value.isInitialized) {
      throw new Error("Dialer not initialized. Call initialize() first.");
    }

    if (initializationState.value.isConnecting) {
      console.warn("Dialer connection already in progress");
      return;
    }

    initializationState.value.isConnecting = true;
    initializationState.value.connectionError = null;

    // Set all instance keys to connecting state
    const keysToConnect = instanceKeysToStart;

    for (const key of keysToConnect) {
      sipConnectionState.value[key] = {
        isConnected: false,
        isConnecting: true,
      };
    }

    try {
      await pressoneDialer.value.start(defaultInstanceKey, instanceKeysToStart);
    } catch (error) {
      // Reset all instance keys to disconnected state on error
      for (const key of keysToConnect) {
        sipConnectionState.value[key] = {
          isConnected: false,
          isConnecting: false,
        };
      }

      initializationState.value.connectionError = "Failed to start dialer";
      throw error;
    } finally {
      initializationState.value.isConnecting = false;
    }
  };

  const stopDialer = async (instanceKeysToStop?: string[]) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      if (callState.value.callPhase === "ACTIVE") {
        endCall();
      }

      await pressoneDialer.value.stop(instanceKeysToStop);

      pressoneDialer.value.removeAllListeners();
    } finally {
      // Reset all instance keys to disconnected state
      const keysToStop =
        instanceKeysToStop || Object.keys(sipConnectionState.value);

      for (const key of keysToStop) {
        sipConnectionState.value[key] = {
          isConnected: false,
          isConnecting: false,
        };
      }

      // Reset initialization state
      resetInitializationState();
      resetCallState();
      clearAllTimeouts();

      pressoneDialer.value = null;
    }
  };

  const makeCall = async ({
    number,
    instanceKey,
    callerIdNumber,
  }: {
    number?: string;
    instanceKey?: string;
    callerIdNumber?: string;
  }) => {
    try {
      // Core validation: Check if dialer is initialized
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      // Core validation: Check connection status
      if (instanceKey) {
        // If a specific instance key is provided, check if that instance is connected
        const targetInstanceStatus = sipConnectionState.value[instanceKey];

        if (!targetInstanceStatus?.isConnected) {
          throw new Error(
            `Target instance key ${instanceKey} is not connected`
          );
        }
      } else {
        // If no specific instance key, check if any instance is connected
        const anyConnected = Object.values(sipConnectionState.value).some(
          (status) => status.isConnected
        );

        if (!anyConnected) {
          throw new Error(
            "Your line is not connected. Please check your internet connection and try again"
          );
        }
      }

      // Core validation: Check if number is provided
      if (!number || number.trim() === "") {
        throw new Error("Number is required to make a call");
      }

      // Core validation: Check if user can make calls
      if (!canMakeCall.value) {
        throw new Error("You don't have permission to make calls");
      }

      const callerId =
        callerIdNumber ||
        numberStore.activeNumber?.business_number?.phone_number;

      await pressoneDialer.value.call(number, undefined, instanceKey, callerId);
    } catch (error) {
      handleError({ error, silent: true });
      throw error;
    }
  };

  const answerCall = () => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      const call = callStackState.value.activeCall;

      if (!call) {
        throw new Error("No call found in the call stack");
      }

      pressoneDialer.value.answerCall(call.id);

      void sendUserActivityEvent(
        INBOUND_CALL_EVENTS.CALL_PICKUP_ATTEMPTED,
        callInfo.value
      );
    } catch (error) {
      handleError({ error });
    }
  };

  const answerQueuedCall = ({
    policy,
    callId, // SESSION ID (.id), not  the SIP call id (.callId)
  }: {
    policy: "hold" | "end";
    callId: string;
  }) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      pressoneDialer.value.answerCall(callId, policy);

      void sendUserActivityEvent(
        INBOUND_CALL_EVENTS.CALL_PICKUP_ATTEMPTED,
        callInfo.value
      );
    } catch (error) {
      handleError({ error });
    }
  };

  const endCall = () => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      pressoneDialer.value.endCall();
    } catch (error) {
      handleError({ error });
    } finally {
      clearAllAudioChannel();
    }
  };

  const transferCall = async (extension: number) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      await pressoneDialer.value.blindTransfer(extension);
      void analytics.logEvent(AMPLITUDE_EVENTS.CALL_TRANSFERRED);
    } catch (error) {
      handleError({ error });
    }
  };

  const switchToCall = ({
    callId, // SESSION ID (.id), not the SIP call id (.callId)
    policy,
  }: {
    callId: string;
    policy: "hold" | "end";
  }) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      pressoneDialer.value.switchToCall(callId, policy);
    } catch (error) {
      handleError({ error });
    }
  };

  // SESSION ID, not the SIP call id
  const rejectQueuedCall = (callId: string) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      pressoneDialer.value.endCall(callId);
    } catch (error) {
      handleError({ error });
    }
  };

  const rejectIncomingCall = () => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      const call = callStackState.value.activeCall;

      if (!call) {
        throw new Error("No call found in the call stack");
      }

      // pressoneDialer.value.rejectCall(call.id) is not working, this is a workaround

      answerCall();

      addTimeout(
        setTimeout(() => {
          endCall();
        }, 1000)
      );
    } catch (error) {
      handleError({ error });
    }
  };

  const toggleMute = () => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      if (pressoneDialer.value.isActiveCallMuted()) {
        pressoneDialer.value.unmuteActiveCall();
        callState.value.isMuted = false;
      } else {
        pressoneDialer.value.muteActiveCall();
        callState.value.isMuted = true;
        void analytics.logEvent(AMPLITUDE_EVENTS.CALL_MUTED);
      }
    } catch (error) {
      handleError({ error });
    }
  };

  const toggleHold = () => {
    try {
      if (!pressoneDialer.value) {
        return;
      }

      if (pressoneDialer.value.isActiveCallHeld()) {
        pressoneDialer.value.unholdActiveCall();
        callState.value.isHeld = false;
      } else {
        pressoneDialer.value.holdActiveCall();
        callState.value.isHeld = true;
        void analytics.logEvent(AMPLITUDE_EVENTS.CALL_HELD);
      }
    } catch (error) {
      handleError({ error });
    }
  };

  const sendDTMF = (digit: number | string) => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      dtmfInput.value.push(digit);
      pressoneDialer.value.sendDTMF(digit.toString());
    } catch (error) {
      handleError({ error });
    }
  };

  const clearDTMF = () => {
    dtmfInput.value = [];
  };

  const removeLastDTMF = () => {
    dtmfInput.value.pop();
  };

  const sendAllDTMF = () => {
    try {
      if (!pressoneDialer.value) {
        throw new Error("Dialer not initialized");
      }

      // Send all DTMF tones
      pressoneDialer.value?.sendDTMF(dtmfInput.value.join(""));

      // Clear the input after sending
      dtmfInput.value = [];
    } catch (error) {
      handleError({ error });
    }
  };

  // UI actions
  const showDialerModal = () => {
    uiState.value.showDialerModal = true;
  };

  const hideDialerModal = () => {
    uiState.value.showDialerModal = false;
  };

  const toggleDialerModal = () => {
    uiState.value.showDialerModal = !uiState.value.showDialerModal;
  };

  const showDialerDrawer = () => {
    uiState.value.showDialerDrawer = true;
  };

  const hideDialerDrawer = () => {
    uiState.value.showDialerDrawer = false;
  };

  const toggleDialerDrawer = () => {
    uiState.value.showDialerDrawer = !uiState.value.showDialerDrawer;
  };

  const toggleDTMFPad = () => {
    uiState.value.showDTMFPad = !uiState.value.showDTMFPad;
  };

  const toggleActiveCallInterfaceMinimized = () => {
    uiState.value.isActiveCallInterfaceMinimized =
      !uiState.value.isActiveCallInterfaceMinimized;
  };

  const showOutboundModal = () => {
    uiState.value.showOutboundModal = true;
  };

  const hideOutboundModal = () => {
    uiState.value.showOutboundModal = false;
  };

  const clearNumberToCall = () => {
    callState.value.numberToCall = "";
  };

  const setNumberToCall = (number?: string) => {
    try {
      if (!number) {
        throw new Error("Number is required to initialize the dialer");
      }

      callState.value.numberToCall = number;
    } catch (error) {
      handleError({ error });
    }
  };

  const toggleSpeaker = () => {
    callState.value.isSpeakerOff = !callState.value.isSpeakerOff;
    // TODO: Implement speaker toggle
  };

  // Enhanced UI methods
  const setCallVolume = (volume: number) => {
    callState.value.callVolume = volume;
    // TODO: Apply to audio system when available
  };

  const setMicVolume = (volume: number) => {
    callState.value.micVolume = volume;
    // TODO: Apply to microphone when available
  };

  const resetVolumes = () => {
    callState.value.callVolume = 80;
    callState.value.micVolume = 100;
  };

  const toggleVolumeControls = () => {
    callState.value.showVolumeControls = !callState.value.showVolumeControls;
  };

  const stopCallQueueTimer = () => {
    if (callQueueState.value.callQueueWaitingTimer) {
      clearInterval(callQueueState.value.callQueueWaitingTimer);
    }

    callQueueState.value.callQueueWaitingTime = callQueueWaitingTime;
  };

  const startCallQueueTimer = () => {
    stopCallQueueTimer();

    if (callQueueState.value.callTriggeredBy === "outbound_call_queue") {
      callQueueState.value.callQueueWaitingTimer = setInterval(() => {
        callQueueState.value.callQueueWaitingTime -= 1;
      }, 1000);
    }
  };

  const setCallTriggeredBy = (
    callTriggeredBy: "regular_call" | "outbound_call_queue"
  ) => {
    callQueueState.value.callTriggeredBy = callTriggeredBy;
  };

  const setCallQueueWaitingTime = (callQueueWaitingTime: number) => {
    callQueueState.value.callQueueWaitingTime = callQueueWaitingTime;
  };

  // Initialization state management methods
  const setInitializationError = (error: string | null) => {
    initializationState.value.connectionError = error;
  };

  const clearInitializationError = () => {
    initializationState.value.connectionError = null;
  };

  const resetCallQueueState = () => {
    stopCallQueueTimer();

    callQueueState.value = {
      ...initialCallQueueState,
    };
  };

  const $reset = () => {
    resetInitializationState();
    resetCallState();
    clearAllTimeouts();

    sipCredentials.value = {
      ...initialDialerSipCredentials,
    };

    sipConnectionState.value = {
      ...initialDialerSipConnectionState,
    };

    pressoneDialer.value = initialPressOneDialer;
  };

  return {
    // State
    initializationState: computed(() => initializationState.value),
    uiState: computed(() => uiState.value),
    callState: computed(() => callState.value),
    sipCredentials: computed(() => sipCredentials.value),
    sipConnectionState: computed(() => sipConnectionState.value),
    dtmfInput: computed(() => dtmfInput.value),
    pressoneDialer: computed(() => pressoneDialer.value),
    callStackState: computed(() => callStackState.value),
    callQueueState: computed(() => callQueueState.value),

    // Computed
    activeCallBusinessId,
    callInfo,
    canMakeCall,
    phonesConnected,
    liveCallConnectionStatus,
    noTrunkCreds,
    activePhoneConnectionStatus,
    callStatus,

    // Actions
    initializeDialer,
    startDialer,
    stopDialer,
    makeCall,
    answerCall,
    answerQueuedCall,
    endCall,
    toggleMute,
    toggleHold,
    toggleSpeaker,
    sendDTMF,
    clearDTMF,
    removeLastDTMF,
    sendAllDTMF,
    transferCall,
    switchToCall,
    rejectQueuedCall,
    rejectIncomingCall,

    // Business Logic Actions
    updateBusinessBalance,
    scheduleBalanceUpdate,
    setupTestCallTimeout,
    addTimeout,
    clearTimeout,
    clearAllTimeouts,

    // UI Actions
    showDialerModal,
    hideDialerModal,
    toggleDialerModal,
    toggleDTMFPad,
    toggleActiveCallInterfaceMinimized,
    showOutboundModal,
    hideOutboundModal,
    showDialerDrawer,
    hideDialerDrawer,
    toggleDialerDrawer,
    clearNumberToCall,
    setNumberToCall,
    // Enhanced UI Actions
    setCallVolume,
    setMicVolume,
    resetVolumes,
    toggleVolumeControls,

    // Call Queue Actions
    startCallQueueTimer,
    stopCallQueueTimer,
    setCallTriggeredBy,
    setCallQueueWaitingTime,
    resetCallQueueState,

    // Initialization state management
    setInitializationError,
    clearInitializationError,
    resetInitializationState,

    // Reset
    $reset,
  };
});
